import { Box, Grid, useMediaQuery } from "@mui/material";
import React, { useEffect } from "react";
import theme from "../../theme";
import { Field, Form, Formik } from "formik";
import { useDispatch, useSelector } from "../../store";
import {
  editAttachmentType,
  postAttachmentType,
} from "../../slices/attachmentType";
import * as Yup from "yup";
import { Inputs } from "../formInputs";
import { Base } from "../base";
import AttachmentType, {
  AttachmentCopyTypes,
} from "../../types/attachmentType";
import { getJobTypes } from "../../slices/jobType";
import { getJobStatuses } from "../../slices/jobStatus";
import { getAttachmentCopyTypes } from "../../slices/options";
import KeyValue from "../../types/keyValue";

interface Props {
  close: () => void;
  onSave: () => void;
}

const AddEditAttachmentTypeForm: React.FC<Props> = (props) => {
  const { close, onSave } = props;
  const dispatch = useDispatch();
  const { attachmentType, status } = useSelector(
    (store) => store.attachmentType,
  );
  const { jobTypes } = useSelector((store) => store.jobType);
  const { jobStatuses } = useSelector((store) => store.jobStatus);
  const { attachmentCopyTypes } = useSelector((store) => store.options);
  const xs = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    dispatch(getJobTypes());
    dispatch(getJobStatuses());
    dispatch(getAttachmentCopyTypes());
  }, [dispatch]);

  const handleSubmit = (values: any) => {
    if (attachmentType.id === 0) {
      dispatch(postAttachmentType(values)).then(({ meta }) => {
        if (meta.requestStatus === "fulfilled") {
          updateAfterChanged();
        }
      });
    } else {
      dispatch(editAttachmentType(values)).then(({ meta }) => {
        if (meta.requestStatus === "fulfilled") {
          updateAfterChanged();
        }
      });
    }
  };

  const updateAfterChanged = () => {
    close();
    if (xs) {
      onSave();
    }
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Required"),
    copy: Yup.bool().nullable(),
    copyJobType: Yup.object().when("copy", {
      is: (value: boolean) => value,
      then: (schema) => schema.required("Required").typeError("Required"),
    }),
    attachmentCopyType: Yup.object().when("copyJobType", {
      is: (value: KeyValue) => value && value?.id && value?.id > 0,
      then: (schema) => schema.required("Required").typeError("Required"),
    }),
    copyJobTypeStatus: Yup.object().when("attachmentCopyType", {
      is: (value: KeyValue) =>
        value &&
        value?.id &&
        (value?.id === AttachmentCopyTypes.CreationMonth ||
          value?.id === AttachmentCopyTypes.CreatorYear),
      then: (schema) => schema.required("Required").typeError("Required"),
    }),
  });

  interface CustomAttachmentType extends AttachmentType {
    copy?: boolean;
  }

  const initialValues: CustomAttachmentType = {
    ...attachmentType,
    copy: !!attachmentType.copyJobType?.id,
    copyJobType: !!attachmentType.copyJobType?.id
      ? attachmentType.copyJobType
      : undefined,
    copyJobTypeStatus: !!attachmentType.copyJobTypeStatus?.id
      ? attachmentType.copyJobTypeStatus
      : undefined,
    attachmentCopyType: !!attachmentType.attachmentCopyType?.id
      ? attachmentType.attachmentCopyType
      : undefined,
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        width: 300,
        borderRadius: "10px",
        padding: "15px",
      }}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({ values }) => (
          <Form noValidate>
            <Grid container>
              <Grid size={12}>
                <Field
                  component={Inputs.Text}
                  name="name"
                  label="Name"
                  required
                />
              </Grid>
              <Grid>
                <Field
                  component={Inputs.Checkbox}
                  name="copy"
                  label="Copy on Upload to Another Job?"
                />
              </Grid>
              {!!values.copy && (
                <Grid size={12}>
                  <Grid container>
                    <Grid size={12}>
                      <Field
                        component={Inputs.Dropdown}
                        options={jobTypes}
                        name="copyJobType"
                        label="Copy To"
                      />
                    </Grid>
                    {!!values.copyJobType?.id && (
                      <Grid size={12}>
                        <Field
                          component={Inputs.Dropdown}
                          options={attachmentCopyTypes}
                          name="attachmentCopyType"
                          label="Copy Destination"
                        />
                      </Grid>
                    )}
                    {!!values.attachmentCopyType?.id &&
                      (values.attachmentCopyType?.id ===
                        AttachmentCopyTypes.CreationMonth ||
                        values.attachmentCopyType?.id ===
                          AttachmentCopyTypes.CreatorYear) && (
                        <Grid size={12}>
                          <Field
                            component={Inputs.Dropdown}
                            options={jobStatuses.filter(
                              (s) => s.jobTypeId === values.copyJobType?.id,
                            )}
                            name="copyJobTypeStatus"
                            label="Copy to Status"
                          />
                        </Grid>
                      )}
                  </Grid>
                </Grid>
              )}
            </Grid>
            <Grid container direction={"row"} justifyContent={"space-between"}>
              <Grid>
                <Base.Button text="Cancel" type="secondary" onClick={close} />
              </Grid>
              <Grid>
                <Inputs.Submit
                  isSubmitting={status === "loading"}
                  text={"Save"}
                />
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default AddEditAttachmentTypeForm;
